import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Stack, type Theme, Tooltip } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { InlineAlert } from "@src/appV2/redesign/components/InlineAlert/InlineAlert";
import { Text } from "@src/appV2/redesign/components/Text";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { type UseModalState } from "@src/lib/ui-react";

import { CredentialAccountStatusChip } from "./components/CredentialAccountStatusChip";
import { useCredentialAccountData } from "./hooks/useCredentialAccountData";
import {
  getActionButtonText,
  getTooltipText,
  shouldShowUsername,
} from "./utils/credentialAccountStatusHelpers";

interface ManageCredentialsModalProps {
  modalState: UseModalState;
  onAction: () => void;
  workerId: string;
  workerName: string | undefined;
  workplaceId: string;
}

export function ManageCredentialsModal(props: ManageCredentialsModalProps) {
  const { modalState, onAction, workerId, workerName, workplaceId } = props;

  const { accountStatus, isError, isLoading, scheduledTimeText, username } =
    useCredentialAccountData({
      workerId,
      workplaceId,
    });

  const title = isDefined(workerName) ? `PCC Account for ${workerName}` : "PCC Account";
  const actionButtonText = getActionButtonText(accountStatus);
  const tooltipText = getTooltipText(accountStatus);
  const isActionDisabled = ["creating", "resetting", "error"].includes(accountStatus);
  const showUsername = shouldShowUsername(accountStatus, username);

  if (isLoading) {
    return (
      <BerlinThemeProvider>
        <Dialog modalState={modalState}>
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </Dialog>
      </BerlinThemeProvider>
    );
  }

  if (isError) {
    return (
      <BerlinThemeProvider>
        <Dialog
          modalState={modalState}
          footerContent={
            <DialogFooter
              primaryAction={{
                label: "Okay",
                onClick: modalState.closeModal,
              }}
            />
          }
        >
          <InlineAlert message="Failed to load credential information. Please try again." />
        </Dialog>
      </BerlinThemeProvider>
    );
  }

  const actionButton = (
    <Button disabled={isActionDisabled} variant="outlined" onClick={onAction}>
      {actionButtonText}
    </Button>
  );

  const footerContent = (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(3),
      })}
    >
      {isDefined(tooltipText) ? (
        <Tooltip arrow title={tooltipText}>
          <span>{actionButton}</span>
        </Tooltip>
      ) : (
        actionButton
      )}
      <Button variant="primary" onClick={modalState.closeModal}>
        Okay
      </Button>
    </Box>
  );

  return (
    <BerlinThemeProvider>
      <Dialog modalState={modalState} title={title} footerContent={footerContent}>
        <Box
          sx={(theme: Theme) => ({
            border: `1px solid ${theme.palette.border?.base}`,
            borderRadius: theme.borderRadius?.large,
            overflow: "hidden",
          })}
        >
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            sx={(theme: Theme) => ({
              borderBottom: showUsername ? `1px solid ${theme.palette.border?.base}` : undefined,
              paddingX: theme.spacing(4),
              paddingY: theme.spacing(3),
            })}
          >
            <Text sx={(theme: Theme) => ({ color: theme.palette.base?.muted })} variant="body2">
              Status
            </Text>
            <CredentialAccountStatusChip
              scheduledTimeText={scheduledTimeText}
              status={accountStatus}
            />
          </Stack>
          {showUsername && (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              sx={(theme: Theme) => ({
                paddingX: theme.spacing(4),
                paddingY: theme.spacing(3),
              })}
            >
              <Text sx={(theme: Theme) => ({ color: theme.palette.base?.muted })} variant="body2">
                Username
              </Text>
              <Text semibold variant="body2">
                {username}
              </Text>
            </Stack>
          )}
        </Box>
      </Dialog>
    </BerlinThemeProvider>
  );
}
